<template>
	<!-- Modal Overlay -->
	<div
		v-if="isVisible"
		class="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 transition-opacity duration-300"
		@click="handleOverlayClick"
	>
		<!-- Modal Container -->
		<div class="flex min-h-full items-center justify-center p-4">
			<div
				class="relative w-full max-w-7xl bg-white rounded-2xl shadow-2xl transform transition-all duration-300 max-h-[90vh] overflow-hidden"
				@click.stop
			>
				<!-- Modal Header -->
				<div
					class="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4"
				>
					<div class="flex items-center justify-between">
						<div class="flex items-center">
							<div
								class="inline-flex items-center justify-center w-10 h-10 bg-blue-600 rounded-xl mr-4"
							>
								<svg
									class="w-5 h-5 text-white"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
									/>
								</svg>
							</div>
							<div>
								<h2 class="text-xl font-bold text-gray-900">
									Compare Insurance Products
								</h2>
								<p class="text-sm text-gray-600">
									{{ selectedComparisonProducts.length }}
									products selected
								</p>
							</div>
						</div>

						<div class="flex items-center space-x-2">
							<button
								@click="clearComparison"
								class="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
							>
								Clear All
							</button>
							<button
								@click="closeModal"
								class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
							>
								<svg
									class="w-5 h-5"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
							</button>
						</div>
					</div>
				</div>

				<!-- Modal Content -->
				<div class="overflow-y-auto max-h-[calc(90vh-80px)]">
					<!-- Empty State -->
					<div
						v-if="selectedComparisonProducts.length === 0"
						class="text-center py-16 px-6"
					>
						<svg
							class="w-16 h-16 mx-auto text-gray-400 mb-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
							/>
						</svg>
						<p class="text-lg text-gray-600 mb-2">
							No products selected for comparison
						</p>
						<p class="text-sm text-gray-500">
							Select at least two products to compare their
							features
						</p>
					</div>

					<!-- Mobile Cards View -->
					<div
						v-if="selectedComparisonProducts.length > 0"
						class="lg:hidden p-4 space-y-4"
					>
						<div
							v-for="(
								product, index
							) in selectedComparisonProducts"
							:key="product.id"
							class="bg-gray-50 rounded-xl p-4 border border-gray-200"
						>
							<div class="flex justify-between items-start mb-4">
								<h3
									class="text-lg font-semibold text-gray-900 flex-1 pr-4"
								>
									{{ product.name }}
								</h3>
								<button
									@click="removeComparisonProduct(product.id)"
									class="text-red-500 hover:text-red-700 p-1"
								>
									<svg
										class="w-4 h-4"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M6 18L18 6M6 6l12 12"
										/>
									</svg>
								</button>
							</div>

							<!-- Core Info -->
							<div class="grid grid-cols-2 gap-4 mb-4">
								<div class="bg-white rounded-lg p-3">
									<p
										class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1"
									>
										Premium
									</p>
									<p class="font-semibold text-gray-900">
										KES {{ formatPremium(product.premium) }}
									</p>
								</div>
								<div class="bg-white rounded-lg p-3">
									<p
										class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1"
									>
										Underwriterrrrrr
									</p>
									<p
										class="font-semibold text-gray-900 text-sm"
									>
										{{ product.underwriter || 'N/A' }}
									</p>
								</div>
								<div class="bg-white rounded-lg p-3">
									<p
										class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1"
									>
										Underwriter
									</p>
									<p
										class="font-semibold text-gray-900 text-sm"
									>
										{{ product.underwriter || 'N/A' }}
									</p>
								</div>
							</div>

							<!-- Expandable Benefits -->
							<div class="space-y-2">
								<div
									v-for="section in expandableSections"
									:key="section.key"
								>
									<button
										@click="
											toggleSection(index, section.key)
										"
										class="w-full flex items-center justify-between py-2 text-left font-medium text-gray-700 border-t border-gray-200"
									>
										<span class="text-sm">{{
											section.title
										}}</span>
										<svg
											class="w-4 h-4 transition-transform duration-200"
											:class="{
												'rotate-180': isSectionExpanded(
													index,
													section.key,
												),
											}"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M19 9l-7 7-7-7"
											/>
										</svg>
									</button>

									<div
										v-show="
											isSectionExpanded(
												index,
												section.key,
											)
										"
										class="pl-4 pb-2"
									>
										<ul
											v-if="
												product[section.key] &&
												product[section.key].length > 0
											"
											class="space-y-1"
										>
											<li
												v-for="benefit in product[
													section.key
												]"
												:key="benefit.id"
												class="text-sm text-gray-600 flex items-start"
											>
												<span
													class="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"
												></span>
												<span>{{ benefit.name }}</span>
											</li>
										</ul>
										<p
											v-else
											class="text-sm text-gray-500 italic"
										>
											No
											{{ section.title.toLowerCase() }}
											listed
										</p>
									</div>
								</div>
							</div>

							<!-- Buy Button -->
							<button
								@click="buyProduct(product)"
								class="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
							>
								<svg
									class="w-4 h-4 mr-2 inline"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
									/>
								</svg>
								Buy Now
							</button>
						</div>
					</div>

					<!-- Desktop Table View -->
					<div
						v-if="selectedComparisonProducts.length > 0"
						class="hidden lg:block p-6"
					>
						<div class="overflow-x-auto">
							<table
								class="w-full text-sm border-separate border-spacing-0"
							>
								<thead>
									<tr class="bg-gray-50">
										<th
											class="p-4 text-left font-semibold text-gray-900 border-b border-gray-200 min-w-[140px] sticky left-0 bg-gray-50 z-10"
										>
											Product
										</th>
										<th
											v-for="product in selectedComparisonProducts"
											:key="`header-${product.id}`"
											class="p-4 text-left font-semibold text-gray-900 border-b border-gray-200 min-w-[200px] relative"
										>
											<div
												class="flex items-start justify-between"
											>
												<span
													class="font-medium text-gray-900 pr-2 leading-tight"
													>{{ product.name }}</span
												>
												<button
													@click="
														removeComparisonProduct(
															product.id,
														)
													"
													class="text-red-500 hover:text-red-700 flex-shrink-0 p-0.5"
												>
													<svg
														class="w-4 h-4"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															stroke-linecap="round"
															stroke-linejoin="round"
															stroke-width="2"
															d="M6 18L18 6M6 6l12 12"
														/>
													</svg>
												</button>
											</div>
										</th>
									</tr>
								</thead>
								<tbody>
									<tr
										class="border-b border-gray-100 hover:bg-gray-50/50"
									>
										<td
											class="p-4 font-medium text-gray-900 sticky left-0 bg-white"
										>
											Premium
										</td>
										<td
											v-for="product in selectedComparisonProducts"
											:key="`premium-${product.id}`"
											class="p-4 font-semibold text-green-600"
										>
											KES
											{{ formatPremium(product.premium) }}
										</td>
									</tr>
									<tr
										class="border-b border-gray-100 hover:bg-gray-50/50"
									>
										<td
											class="p-4 font-medium text-gray-900 sticky left-0 bg-white"
										>
											Base Premium Rate
										</td>
										<td
											v-for="product in selectedComparisonProducts"
											:key="`premium-${product.id}`"
											class="p-4 font-semibold text-green-600"
										>
											{{
												product.basePremiumRateLabel ||
												'Not specified'
											}}
										</td>
									</tr>
									<tr
										class="border-b border-gray-100 hover:bg-gray-50/50"
									>
										<td
											class="p-4 font-medium text-gray-900 sticky left-0 bg-white"
										>
											Underwriter
										</td>
										<td
											v-for="product in selectedComparisonProducts"
											:key="`underwriter-${product.id}`"
											class="p-4 text-gray-700"
										>
											{{
												product.underwriter ||
												'Not specified'
											}}
										</td>
									</tr>
									<tr
										class="border-b border-gray-100 hover:bg-gray-50/50"
									>
										<td
											class="p-4 font-medium text-gray-900 sticky left-0 bg-white"
										>
											Core Benefits
										</td>
										<td
											v-for="product in selectedComparisonProducts"
											:key="`core-${product.id}`"
											class="p-4 align-top"
										>
											<ul
												class="space-y-1"
												v-if="
													product.coreBenefits &&
													product.coreBenefits
														.length > 0
												"
											>
												<li
													v-for="benefit in product.coreBenefits"
													:key="
														benefit.id ||
														benefit.name
													"
													class="text-gray-600 flex items-start text-xs"
												>
													<span
														class="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2 flex-shrink-0"
													></span>
													<span>{{
														benefit.name
													}}</span>
												</li>
											</ul>
											<span
												v-else
												class="text-gray-400 italic text-xs"
												>No core benefits</span
											>
										</td>
									</tr>
									<tr
										class="border-b border-gray-100 hover:bg-gray-50/50"
									>
										<td
											class="p-4 font-medium text-gray-900 sticky left-0 bg-white"
										>
											Optional Benefits
										</td>
										<td
											v-for="product in selectedComparisonProducts"
											:key="`optional-${product.id}`"
											class="p-4 align-top"
										>
											<ul
												class="space-y-1"
												v-if="
													product.optionalBenefits &&
													product.optionalBenefits
														.length > 0
												"
											>
												<li
													v-for="benefit in product.optionalBenefits"
													:key="
														benefit.id ||
														benefit.name
													"
													class="text-gray-600 flex items-start text-xs"
												>
													<span
														class="w-1.5 h-1.5 bg-green-500 rounded-full mt-1.5 mr-2 flex-shrink-0"
													></span>
													<span>{{
														benefit.name
													}}</span>
												</li>
											</ul>
											<span
												v-else
												class="text-gray-400 italic text-xs"
												>No optional benefits</span
											>
										</td>
									</tr>
									<tr
										class="border-b border-gray-100 hover:bg-gray-50/50"
									>
										<td
											class="p-4 font-medium text-gray-900 sticky left-0 bg-white"
										>
											Excesses
										</td>
										<td
											v-for="product in selectedComparisonProducts"
											:key="`excess-${product.id}`"
											class="p-4 align-top"
										>
											<ul
												v-if="
													product.excesses &&
													product.excesses.length
												"
												class="space-y-1"
											>
												<li
													v-for="excess in product.excesses"
													:key="excess.id"
													class="text-gray-600 flex items-start text-xs"
												>
													<span
														class="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-1.5 mr-2 flex-shrink-0"
													></span>
													<span>{{
														excess.name
													}}</span>
												</li>
											</ul>
											<span
												v-else
												class="text-gray-400 italic text-xs"
												>No excesses</span
											>
										</td>
									</tr>

									<tr>
										<td
											class="p-4 font-medium text-gray-900 sticky left-0 bg-white"
										>
											Action
										</td>
										<td
											v-for="product in selectedComparisonProducts"
											:key="`action-${product.id}`"
											class="p-4"
										>
											<button
												@click="buyProduct(product)"
												class="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-3 rounded-lg transition-colors duration-200 text-sm"
											>
												<svg
													class="w-4 h-4 mr-1 inline"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														stroke-width="2"
														d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
													/>
												</svg>
												Buy Now
											</button>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { FetchClassProductDetails } from '../../../api/insurance';

export default {
	name: 'CompareProductsModal',
	props: {
		visible: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			expandedSections: {},
			// showDebugInfo: false,  Set to true temporarily to debug
			expandableSections: [
				{ key: 'coreBenefits', title: 'Core Benefits' },
				{ key: 'optionalBenefits', title: 'Optional Benefits' },
			],
		};
	},
	computed: {
		...mapState('comparison', ['selectedComparisonProducts']),
		...mapState('insurance/motorPrivate', [
			'selectedCoverType',
			'vehicleValue',
		]),

		isVisible() {
			return this.visible;
		},
	},
	watch: {
		selectedComparisonProducts: {
			immediate: true,
			handler(newProducts) {
				//console.log('Modal: Products changed', newProducts); // Debug log
				if (newProducts && newProducts.length > 0) {
					const newExpandedSections = {};
					newProducts.forEach((product, index) => {
						newExpandedSections[index] = {};
						this.expandableSections.forEach((section) => {
							newExpandedSections[index][section.key] = false;
						});
					});
					this.expandedSections = newExpandedSections;
				}
			},
		},

		isVisible: {
			immediate: true,
			handler(newVal) {
				if (newVal) {
					//console.log('Modal opened with products:', this.selectedComparisonProducts); // Debug log
					document.addEventListener('keydown', this.handleEscapeKey);
					document.body.style.overflow = 'hidden';
				} else {
					document.removeEventListener(
						'keydown',
						this.handleEscapeKey,
					);
					document.body.style.overflow = '';
				}
			},
		},
	},
	methods: {
		...mapActions('comparison', [
			'removeComparisonProduct',
			'clearComparisonProducts',
		]),
		...mapActions('insurance/motorPrivate', [
			'setSelectedProduct',
			'setVehicleValue',
			'setErrors',
		]),

		formatPremium(premium) {
			if (!premium && premium !== 0) return 'N/A';
			if (typeof premium === 'number') return premium.toLocaleString();
			return premium;
		},

		isSectionExpanded(productIndex, sectionKey) {
			return (
				this.expandedSections[productIndex] &&
				this.expandedSections[productIndex][sectionKey]
			);
		},

		toggleSection(productIndex, sectionKey) {
			if (!this.expandedSections[productIndex]) {
				this.$set(this.expandedSections, productIndex, {});
			}
			const currentState =
				this.expandedSections[productIndex][sectionKey] || false;
			this.$set(
				this.expandedSections[productIndex],
				sectionKey,
				!currentState,
			);
		},

		async buyProduct(product) {
			if (!this.selectedCoverType) {
				this.setErrors({
					selectedCoverType: 'Please select a cover type.',
				});
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Selection Required',
					message: 'Please select a cover type to proceed.',
					duration: 3000,
				});
				return;
			}

			try {
				const res = await FetchClassProductDetails(product.id);
				if (res.status) {
					this.setSelectedProduct(product);
					this.$store.dispatch(
						'productType/setProduct',
						res.data.result.data[0],
					);
					if (this.selectedCoverType === 'third-party') {
						this.setVehicleValue(0);
					}
					this.closeModal();
					this.$router.push({ name: 'BasicCoverDetails' });
				}
			} catch (error) {
				console.error('Error fetching product details:', error);
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message:
						'Failed to fetch product details. Please try again.',
					duration: 3000,
				});
			}
		},

		clearComparison() {
			this.clearComparisonProducts();
			Event.$emit('notifications:show', {
				type: 'info',
				title: 'Comparison Cleared',
				message:
					'All products have been removed from the comparison list.',
				duration: 3000,
			});
		},

		handleOverlayClick(event) {
			const modalContent = event.currentTarget.querySelector(
				'.relative.w-full.max-w-7xl',
			);
			if (modalContent && modalContent.contains(event.target)) {
				return;
			}
			this.closeModal();
		},

		closeModal() {
			this.$emit('close');
		},

		handleEscapeKey(event) {
			if (event.key === 'Escape') {
				this.closeModal();
			}
		},

		formatBasePremiumRate(product) {
			if (!product.basePremiumRate) return 'Not specified';

			let type = product.basePremiumRate.rateType;
			let value = product.basePremiumRate.value;

			if (type === 'percentage') {
				return value + '%';
			}

			return 'KSh ' + value;
		},
	},

	mounted() {
		//  console.log('Modal mounted with products:', this.selectedComparisonProducts);
	},

	beforeDestroy() {
		document.removeEventListener('keydown', this.handleEscapeKey);
		document.body.style.overflow = '';
	},
};
</script>

<style scoped>
/* Modal animations */
.modal-enter-active,
.modal-leave-active {
	transition: opacity 0.3s ease;
}

.modal-enter,
.modal-leave-to {
	opacity: 0;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
	transition: transform 0.3s ease;
}

.modal-enter .modal-container,
.modal-leave-to .modal-container {
	transform: scale(0.95) translateY(20px);
}

/* Table styles */
table {
	border-collapse: separate;
	border-spacing: 0;
}

th:first-child,
td:first-child {
	position: sticky;
	left: 0;
	z-index: 1;
}

/* Custom scrollbar for modal */
.overflow-y-auto::-webkit-scrollbar {
	width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
	background: #f1f5f9;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
	background: #cbd5e1;
	border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
	background: #94a3b8;
}

.overflow-x-auto::-webkit-scrollbar {
	height: 6px;
}

.overflow-x-auto::-webkit-scrollbar-track {
	background: #f1f5f9;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
	background: #cbd5e1;
	border-radius: 3px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
	background: #94a3b8;
}

/* Mobile responsive */
@media (max-width: 768px) {
	.modal-container {
		margin: 1rem;
		max-height: calc(100vh - 2rem);
	}
}
</style>
